import { html, SP } from '../../html.js';
import type { VNode } from '../../html.js';
import { OUTCOMES } from '../api.js';
import type { Outcome, OutcomeCounts, ToolAggregate, UsageWindow } from '../api.js';
import type { DashboardActions, DashboardState } from '../store.js';
import { Empty, ErrorState, Scroller, Skeleton, Stat, When, fmtInt, fmtMs } from '../ui/bits.js';
import { Chart, OUTCOME_LABEL } from '../ui/chart.js';

/**
 * Traffic: counters, and the word "counters" on the page.
 *
 * It was called Usage, which is what the operator who wrote the store calls it.
 * A customer reads "usage" as a bill and "traffic" as what agents did, and the
 * second is what this screen actually reports — so the customer's word wins and
 * the URL follows it (`#/traffic`, with `#/usage` still parsing).
 *
 * THIS IS NOT BILLING AND THE SCREEN SAYS SO. `/pricing` publishes a price per
 * ten thousand calls, and there is no meter behind it — no card field, no
 * invoice, no ledger that money moves through. A dashboard that printed a call
 * total under a heading like "This month" would be the same lie the pricing page
 * refuses to tell, in a place where an operator is more likely to believe it.
 * So the number is labelled as what it is: a count of tool calls kept by
 * `apps/server/src/usage/store.ts`.
 *
 * The billable set is READ from the server rather than restated from memory.
 * `BILLABLE_OUTCOMES` in that file is `ok`, `empty` and `stale` — work we did —
 * and it excludes `error` and `rate_limited`, which are calls that cost nobody a
 * fetch. Splitting the window that way here means the day an invoice exists, the
 * number an operator has been looking at is the number it is built from.
 *
 * The chart is the server's own zero-filled series, so a quiet day is a zero
 * column rather than a gap between two busy ones.
 */

const DAY_CHOICES = [7, 14, 30, 90];

/** apps/server/src/usage/store.ts — `BILLABLE_OUTCOMES`. */
const BILLABLE = ['ok', 'empty', 'stale'] as const;

/**
 * The outcome split for the window.
 *
 * `UsageReport` carries the split per DAY and not on the window itself, so the
 * total is summed from the series the server sent rather than left as five
 * zeroes under a chart that plainly shows traffic. It is a sum of reported
 * numbers, not an estimate: with no series there is nothing to sum and the
 * screen says the split was not reported.
 */
function splitOf(usage: UsageWindow): { counts: OutcomeCounts; derived: boolean } | null {
  const declared = OUTCOMES.some((o) => usage.byOutcome[o] > 0);
  if (declared) return { counts: usage.byOutcome, derived: false };
  if (!usage.series.length) return null;
  const counts = {} as OutcomeCounts;
  for (const o of OUTCOMES) counts[o] = usage.series.reduce((n, p) => n + p.byOutcome[o], 0);
  if (!OUTCOMES.some((o) => counts[o] > 0)) return null;
  return { counts, derived: true };
}

/**
 * Rows and milliseconds for the window.
 *
 * `UsageReport` reports `calls` and nothing else at the top level, so these two
 * are summed from the same series the chart draws. The distinction that matters
 * is between a sum and a guess: with no series there is nothing to add up, and
 * the tile prints an em dash and says the field was not reported rather than a
 * zero, which an operator would read as "no rows ever came back".
 */
function totalOf(usage: UsageWindow, field: 'rows' | 'ms'): { value: number | null; derived: boolean } {
  if (usage[field] > 0) return { value: usage[field], derived: false };
  if (!usage.series.length) return { value: null, derived: false };
  const sum = usage.series.reduce((n, p) => n + p[field], 0);
  return { value: sum, derived: true };
}

/**
 * The share of `empty` at which a tool stops looking like a quiet Tuesday.
 *
 * Half is a judgement, not a measurement, and it is deliberately loose: the
 * screen NAMES the tools rather than grading them, so a tool listed here is an
 * invitation to press "Run" on its card, never a verdict. Below half, a mix of
 * empty and ok is what a search tool over a real catalogue does all day.
 */
const HOLLOW_SHARE = 0.5;

/**
 * The by-tool table's column heads, which are not `OUTCOME_LABEL`.
 *
 * The chart's legend labels an outcome; a column head labels what happened to
 * a call, and "Empty" over a column of numbers reads as "no data" when it means
 * the opposite — the tool answered, with nothing in it. That distinction is the
 * whole reading this table exists to make, so the two vocabularies are kept
 * apart rather than one being bent to serve both.
 *
 * Typed as a total map over `Outcome` on purpose: the columns are rendered by
 * walking `OUTCOMES`, so a sixth outcome added to the rollup fails the
 * typecheck here instead of silently vanishing from a table that has one
 * hardcoded column per outcome.
 */
const TOOL_HEAD: Record<Outcome, string> = {
  ok: 'Answered',
  empty: 'Answered nothing',
  stale: 'Stale',
  rate_limited: 'Refused',
  error: 'Failed',
};

/**
 * Tools that answered nothing more often than they answered.
 *
 * This is the one reading of the split that a person cannot do by eye from a
 * stacked bar, and it is the reading worth having: `ok` and `empty` are the
 * same HTTP 200 and the second is what a CSS selector that stopped matching
 * looks like from the meter. The rollup keeps them apart on purpose — see the
 * header of `apps/server/src/usage/store.ts` — and a screen that folded them
 * back together would throw away the only rot signal the counters carry.
 *
 * Sorted by how many calls came back hollow rather than by share, because ten
 * empty answers out of ten is a tool nobody uses and four hundred out of six
 * hundred is a tool everybody uses that has quietly stopped working.
 */
function hollowTools(byTool: readonly ToolAggregate[]): ToolAggregate[] {
  return byTool
    .filter((t) => t.calls > 0 && t.byOutcome.empty / t.calls >= HOLLOW_SHARE)
    .sort((a, b) => b.byOutcome.empty - a.byOutcome.empty);
}

function pct(part: number, whole: number): string {
  if (!whole) return '—';
  return `${((part / whole) * 100).toFixed(part && part / whole < 0.01 ? 2 : 0)}%`;
}

/**
 * A tool's name, pointing at the site whose tools tab holds its card.
 *
 * The row knows which site it belongs to two ways: the selector, when one site
 * is being looked at, and `tenantId` on the aggregate, when the window is the
 * whole host. Before the second was carried the host-wide table could link
 * nothing at all, so every row was inert text — which is the defect the
 * inventory records for this screen.
 */
function toolLink(t: ToolAggregate, selected: string | null): VNode {
  const tenantId = selected ?? t.tenantId;
  if (!tenantId) return html`<code>${t.tool}</code>`;
  return html`<a href=${`#/sites/${encodeURIComponent(tenantId)}/tools`}><code>${t.tool}</code></a>`;
}

export function TrafficScreen(props: { state: DashboardState; actions: DashboardActions }): VNode {
  const { state, actions } = props;
  const tenants = state.overview?.tenants ?? [];

  const controls = html`
    <div class="ucontrols">
      <label class="ucontrol" for="usage-days">
        <span class="cardlabel">Window</span>
        <select
          id="usage-days"
          class="ucontrol__select"
          onChange=${(e: Event) => actions.setUsageDays(Number((e.target as HTMLSelectElement).value))}
        >
          ${DAY_CHOICES.map(
            (d) => html`<option value=${String(d)} selected=${d === state.usageDays}>
              last ${d} days
            </option>`,
          )}
        </select>
      </label>
      <label class="ucontrol" for="usage-tenant">
        <span class="cardlabel">Site</span>
        <select
          id="usage-tenant"
          class="ucontrol__select"
          onChange=${(e: Event) =>
            actions.setUsageTenant((e.target as HTMLSelectElement).value || null)}
        >
          <option value="" selected=${!state.usageTenantId}>every site on this host</option>
          ${tenants.map(
            (t) => html`<option value=${t.tenantId} selected=${t.tenantId === state.usageTenantId}>
              ${t.displayName} (${t.tenantId})
            </option>`,
          )}
        </select>
      </label>
    </div>
  `;

  if (state.error) {
    return html`
      <section class="wrap screen">
        <h1 class="screen__h" tabindex="-1" data-focus-target>Traffic</h1>
        ${ErrorState({
          message: state.error,
          label: 'Could not load the window.',
          body: 'There is no cached copy and no recorded window to fall back on, so this is a message and not a chart of zeroes — a zero here would read as "nobody called anything", which is a different fact from "the counter could not be read".',
          onRetry: () => actions.reload(),
        })}
      </section>
    `;
  }

  if (!state.usage) {
    return html`
      <section class="wrap screen">
        <h1 class="screen__h" tabindex="-1" data-focus-target>Traffic</h1>
        ${Skeleton({ rows: 4, label: 'Loading the window…' })}
      </section>
    `;
  }

  const u: UsageWindow = state.usage;
  const split = splitOf(u);
  const rows = totalOf(u, 'rows');
  const ms = totalOf(u, 'ms');
  const billable = split ? BILLABLE.reduce((n, o) => n + split.counts[o], 0) : 0;
  const excluded = Math.max(0, u.calls - billable);
  const scope = state.usageTenantId
    ? `site ${state.usageTenantId}`
    : 'every site on this host';
  const hollow = hollowTools(u.byTool);
  const now = Date.now();

  return html`
    <section class="wrap screen">
      <h1 class="screen__h" tabindex="-1" data-focus-target>
        ${fmtInt(u.calls)} tool ${u.calls === 1 ? 'call' : 'calls'}
      </h1>
      <p class="screen__p">
        ${u.from && u.to ? html`${u.from} → ${u.to}` : `the last ${state.usageDays} days`}${SP}·${SP}
        ${scope}. These are counters, not a bill.
      </p>

      ${controls}

      <div class="stats">
        ${Stat({ label: 'Calls', value: fmtInt(u.calls), note: `${u.series.length} days in the window` })}
        ${Stat({
          label: 'Rows returned',
          value: rows.value === null ? '—' : fmtInt(rows.value),
          note: rows.value === null ? 'not reported for this window' : rows.derived ? 'summed from the daily series' : undefined,
        })}
        ${Stat({
          label: 'Time in tool code',
          value: ms.value === null ? '—' : fmtMs(ms.value),
          note:
            ms.value === null
              ? 'not reported for this window'
              : u.calls
                ? `${fmtMs(ms.value / u.calls)} per call`
                : undefined,
        })}
        ${Stat({
          label: 'Tools called',
          value: fmtInt(u.byTool.length),
          note: u.byTool.length ? undefined : 'nothing has been called in this window',
        })}
      </div>

      <h2 class="sec__h">Calls per day</h2>
      ${Chart({ series: u.series, label: `Calls per day for ${scope}` })}

      <h2 class="sec__h">Outcomes</h2>
      ${split
        ? html`
            ${Scroller({
              label: 'Outcome split',
              children: html`<table class="otable">
              <thead>
                <tr>
                  <th scope="col">Outcome</th>
                  <th scope="col" class="num">Calls</th>
                  <th scope="col" class="num">Share</th>
                  <th scope="col">Counted toward a bill</th>
                  <th scope="col">What it means</th>
                </tr>
              </thead>
              <tbody>
                ${OUTCOMES.map((o) => {
                  const yes = (BILLABLE as readonly string[]).includes(o);
                  return html`<tr>
                    <th scope="row">
                      <span class=${`legend__key legend__key--${o}`} aria-hidden="true"></span>${SP}
                      ${OUTCOME_LABEL[o]}
                    </th>
                    <td class="num">${fmtInt(split.counts[o])}</td>
                    <td class="num">${pct(split.counts[o], u.calls)}</td>
                    <td>
                      ${yes
                        ? html`<span class="bill bill--in">billable</span>`
                        : html`<span class="bill bill--out">excluded</span>`}
                    </td>
                    <td class="otable__why">
                      ${o === 'ok'
                        ? 'The binding ran against the origin and returned rows.'
                        : o === 'empty'
                          ? 'The binding ran and the origin had nothing matching. The fetch cost the same as a full answer.'
                          : o === 'stale'
                            ? 'The origin refused or failed, and a cached answer inside the stale window was served and labelled stale.'
                            : o === 'rate_limited'
                              ? 'Refused by our own limiter before a byte left the box. Nothing was fetched.'
                              : 'The call failed. No answer was delivered.'}
                    </td>
                  </tr>`;
                })}
              </tbody>
            </table>`,
            })}
            <p class="screen__foot">
              ${fmtInt(billable)} of ${fmtInt(u.calls)} calls fall in the billable set
              (<code>ok</code>, <code>empty</code>, <code>stale</code>) and ${fmtInt(excluded)}${SP}
              are excluded (<code>error</code>, <code>rate_limited</code>), as${SP}
              <code>BILLABLE_OUTCOMES</code> in <code>apps/server/src/usage/store.ts</code> defines
              the set. <b>Nothing on this host bills anyone</b>: there is no meter behind the price
              on <code>/pricing</code>, so this split is what an invoice WOULD be built from, not
              one that exists. That page also says a capability returning nothing is not billed,
              while <code>empty</code> is in the server's billable set — the two have to agree
              before either can charge anybody.
              ${split.derived
                ? html`${SP}The split is summed from the daily series the server sent; the window
                    itself carries no total.`
                : null}
            </p>
          `
        : Empty({
            title: 'No outcome split was reported for this window.',
            body: 'The counters exist per day, and none of the days in this window carry one — so nothing is shown rather than five zeroes.',
          })}

      <h2 class="sec__h">By tool</h2>
      ${hollow.length
        ? html`<div class="callout callout--warn" data-tone="signal" data-rule="left" role="status">
            <p class="hollow__h">
              ${hollow.length === 1 ? 'One tool' : `${fmtInt(hollow.length)} tools`} answered nothing
              on at least half of ${hollow.length === 1 ? 'its' : 'their'} calls.
            </p>
            <ul class="hollow__list">
              ${hollow.map(
                (t) => html`<li class="hollow__item">
                  ${toolLink(t, state.usageTenantId)}${SP}—${SP}
                  ${fmtInt(t.byOutcome.empty)} of ${fmtInt(t.calls)} calls
                  (${pct(t.byOutcome.empty, t.calls)}) came back with no rows.
                </li>`,
              )}
            </ul>
            <p class="hollow__p">
              Zero rows is a legitimate answer — a day with nothing on, a search that matched
              nothing — and it is also exactly what a binding whose selector stopped matching looks
              like, because both are an HTTP 200 with an empty list. Nothing here can tell them
              apart; the tool's own card can, by running the binding against the origin now and
              showing you what came back.
            </p>
          </div>`
        : null}
      ${u.byTool.length
        ? Scroller({
            label: 'Calls per tool',
            children: html`
              <table class="utable">
                <thead>
                  <tr>
                    <th scope="col">Tool</th>
                    ${state.usageTenantId ? null : html`<th scope="col">Site</th>`}
                    <th scope="col" class="num">Calls</th>
                    ${OUTCOMES.map((o) => html`<th scope="col" class="num">${TOOL_HEAD[o]}</th>`)}
                    <th scope="col" class="num">Rows</th>
                    <th scope="col" class="num">Avg</th>
                    <th scope="col">Last called</th>
                  </tr>
                </thead>
                <tbody>
                  ${u.byTool.map((t) => {
                    const empty = t.byOutcome.empty;
                    const flagged = t.calls > 0 && empty / t.calls >= HOLLOW_SHARE;
                    return html`<tr class=${flagged ? 'utable__row utable__row--hollow' : 'utable__row'}>
                      <th scope="row">${toolLink(t, state.usageTenantId)}</th>
                      ${state.usageTenantId
                        ? null
                        : html`<td><code class="utable__site">${t.tenantId ?? '—'}</code></td>`}
                      <td class="num">${fmtInt(t.calls)}</td>
                      ${OUTCOMES.map((o) => {
                        const n = t.byOutcome[o];
                        // `empty` is the one column carrying a reading rather
                        // than a count, so it alone shows its share and takes
                        // the flag; the other four are counts and say so.
                        if (o !== 'empty') {
                          return html`<td class=${n ? 'num' : 'num num--zero'}>${fmtInt(n)}</td>`;
                        }
                        return html`<td class=${flagged ? 'num hollowcell' : n ? 'num' : 'num num--zero'}>
                          ${fmtInt(n)}${n
                            ? html`<span class="hollowcell__share">${pct(n, t.calls)}</span>`
                            : null}
                        </td>`;
                      })}
                      <td class="num">${fmtInt(t.rows)}</td>
                      <td class="num">${t.calls ? fmtMs(t.ms / t.calls) : '—'}</td>
                      <td>
                        ${When({ iso: t.lastCalledAt, now, absent: 'not in this window' })}
                      </td>
                    </tr>`;
                  })}
                </tbody>
              </table>
            `,
          })
        : Empty({
            title: 'No tool was called in this window.',
            body: 'That is a real zero: the usage store answered and it has no rows for these days. A longer window, or a site that agents have actually reached, is where the first row comes from.',
            action: { label: 'See your sites', href: '#/sites' },
          })}
    </section>
  `;
}
